import React, { useState, useEffect } from 'react';

function CollapsibleSection({ title, children, defaultOpen = true, onTitleClick }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        className="bookmark-title"
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <span
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setOpen(!open)}
        >
          {open ? '▼' : '▶'}
        </span>
        {onTitleClick ? (
          <span
            className="anchor-link"
            style={{ cursor: 'pointer', flex: 1 }}
            onClick={e => {
              e.stopPropagation();
              onTitleClick();
            }}
          >
            {title}
          </span>
        ) : (
          <span style={{ flex: 1 }}>{title}</span>
        )}
      </div>
      {open && <div style={{ marginLeft: 12 }}>{children}</div>}
    </div>
  );
}

function PrefatoryMaterialTree({ onJump }) {
  const items = [
    { title: "User Note", page: 5 },
    { title: "Abbreviations", page: 6 },
    { title: "Acknowledgements", page: 9 },
    { title: "Introduction", page: 11, children: [ { title: "Organization", page: 11 } ] },
    { title: "Citations, Annotations, and Links", page: 18 },
    { title: "A Note on Specialized Terms for Researchers New to William Blake", page: 20 },
    { title: "Different Blake Journals", page: 22 }
  ];
  return (
    <CollapsibleSection title="Prefatory Material" defaultOpen={true}>
      <ul style={{ paddingLeft: 0, marginTop: 8 }}>
        {items.map((item, idx) => (
          <li key={idx} style={{ marginBottom: 6 }}>
            <span className="bookmark-title anchor-link" onClick={() => onJump(item.page)}>{item.title}</span>
            {item.children && item.children.length > 0 && (
              <ul style={{ listStyle: 'none', paddingLeft: 12, marginTop: 6 }}>
                {item.children.map((child, cidx) => (
                  <li key={`child-${idx}-${cidx}`} style={{ marginBottom: 4 }}>
                    <span className="bookmark-title anchor-link" onClick={() => onJump(child.page)}>{child.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </CollapsibleSection>
  );
}
// Restore BookmarkTree component
// Helper to clean unwanted trailing number/letter patterns (e.g., 32F, 35FF, 39f, 12a, 123)
function cleanBookmarkTitle(title) {
  // Normalize whitespace and remove trailing page codes; handle non-breaking spaces and Unicode combining marks
  if (!title) return '';
  let t = title.replace(/\u00A0/g, ' '); // NBSP -> space
  // Remove trailing page-like codes (e.g., '39F', '35FF', '12a')
  t = t.replace(/(\d+[a-zA-Z]{0,2})$/, '').replace(/\s{2,}/g, ' ').trim();
  // Unicode normalize and strip diacritics
  try {
    t = t.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  } catch (e) {
    // ignore if normalize not supported
  }
  return t;
}

// Normalize titles for robust comparison: lowercase, normalize apostrophes, remove punctuation, collapse whitespace
function normalizeTitle(title) {
  if (!title) return '';
  return title
    .replace(/[’‘]/g, "'")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function BookmarkTree({ bookmarks, onJump }) {
  if (!bookmarks || bookmarks.length === 0) return null;

  // Remove entries that, after cleaning, have an empty title (these render as blank headers).
  // This prevents long sequences of empty headers (e.g., under Exhibition and Sale Catalogues).
  const visibleBookmarks = bookmarks.filter(bm => {
    const cleaned = cleanBookmarkTitle(bm.title || '');
    return cleaned && cleaned.trim() !== '';
  });

  if (visibleBookmarks.length === 0) return null;

  return (
    <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
      {visibleBookmarks.map((bm, idx) => (
        <li key={idx} style={{ marginBottom: 6, marginTop: idx === 0 ? 0 : 8 }}>
          {bm.children && bm.children.length > 0 ? (
            <CollapsibleSection
              title={cleanBookmarkTitle(bm.title)}
              defaultOpen={false}
              onTitleClick={bm.page ? () => onJump(bm.page) : undefined}
            >
              {/* Robust filtering: remove children whose cleaned/normalized title equals parent cleaned/normalized title */}
              {(() => {
                const parentRaw = (bm.title || '').trim();
                const parentClean = cleanBookmarkTitle(parentRaw);
                const parentNorm = normalizeTitle(parentClean);
                const childrenFiltered = bm.children.filter(c => {
                  const childRaw = (c.title || '').trim();
                  const childClean = cleanBookmarkTitle(childRaw);
                  const childNorm = normalizeTitle(childClean);
                  if (childClean === parentClean) return false; // cleaned exact match
                  if (childNorm === parentNorm) return false; // normalized match
                  return true;
                });
                return <BookmarkTree bookmarks={childrenFiltered} onJump={onJump} />;
              })()}
            </CollapsibleSection>
          ) : (
            <span className="bookmark-title anchor-link" onClick={() => onJump(bm.page)}>{cleanBookmarkTitle(bm.title)}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

import axios from 'axios';
import './blakeArchiveStyle.css';
import SelectablePageViewer from './SelectablePageViewer';
import VirtualizedPageList from './VirtualizedPageList';

// Custom sidebar for Prefatory Material
// PrefatorySidebar removed as per user request

function App() {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchPageSize, setSearchPageSize] = useState(50);
  const [searchTotalPages, setSearchTotalPages] = useState(1);
  const [searchTotalResults, setSearchTotalResults] = useState(0);
  const [bookmarks, setBookmarks] = useState([]);
  // Read ?page=... from URL on load
  function getInitialPageNum() {
    const params = new URLSearchParams(window.location.search);
    const p = parseInt(params.get('page'), 10);
    return (p && !isNaN(p) && p > 0) ? p : 1;
  }
  function getInitialHighlight() {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    return q ? q : '';
  }
  const [pageNum, setPageNum] = useState(getInitialPageNum());
  const [highlightQuery, setHighlightQuery] = useState(getInitialHighlight());
  const [showSearchHelp, setShowSearchHelp] = useState(false);
  const [pageImg, setPageImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'continuous'
  const [totalPages, setTotalPages] = useState(0);
  // Handler functions
  const [allPages, setAllPages] = useState([]);

  useEffect(() => {
    // Fetch bookmarks and PDF info on mount
    axios.get('/api/bookmarks').then(res => {
      setBookmarks(res.data.bookmarks);
    });
    axios.get('/api/info').then(res => {
      setTotalPages(res.data.total_pages);
    }).catch(err => {
      console.error('Failed to fetch PDF info:', err);
      setTotalPages(1782); // Fallback to known page count
    });
  }, []);

  useEffect(() => {
    // Fetch page image when pageNum changes with retry logic
    setLoading(true);
    setError('');
    const fetchPage = async (retries = 3) => {
      try {
        const res = await axios.get(`/api/page/${pageNum}`, { responseType: 'blob' });
        setPageImg(URL.createObjectURL(res.data));
        setLoading(false);
        setError('');
      } catch (error) {
        if (retries > 0) {
          console.log(`Retrying page ${pageNum}, attempts left: ${retries}`);
          setError(`Connection issue loading page ${pageNum}, retrying... (${4 - retries}/3)`);
          setTimeout(() => fetchPage(retries - 1), 1000);
        } else {
          console.error(`Failed to load page ${pageNum}:`, error);
          setError(`Failed to load page ${pageNum}. Please check that the backend server is running.`);
          setLoading(false);
        }
      }
    };
    fetchPage();
  }, [pageNum]);

  const fetchSearchResults = async (page = 1, pageSize = searchPageSize) => {
    const res = await axios.get(`/api/search?query=${encodeURIComponent(search)}&page=${page}&page_size=${pageSize}`);
    setResults(res.data.results);
    setSearchPage(page);
    setSearchPageSize(pageSize);
    setSearchTotalPages(res.data.total_pages);
    setSearchTotalResults(res.data.total_results);
  };

  const handleSearch = async () => {
  await fetchSearchResults(1, searchPageSize);
  setHighlightQuery(search || '');
  setSearchModalOpen(true);
  };

  const handleSearchPageChange = (newPage) => {
    fetchSearchResults(newPage, searchPageSize);
  };

  const handleSearchPageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setSearchPageSize(newSize);
    fetchSearchResults(1, newSize);
  };

  // Load all pages for continuous view
  const loadAllPages = async () => {
    if (allPages.length > 0) return;
    setLoading(true);
    setError('');
    const pages = [];
    const failedPages = [];

    // Abort signal so we can cancel outstanding fetches when user navigates away
    const controller = new AbortController();
    // store controller for potential abort from other handlers
    window.__loadAllPagesController = controller;

    const fetchPageWithRetry = async (pageNum, retries = 2, signal = controller.signal) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const response = await axios.get(`/api/page/${pageNum}`, {
            responseType: 'blob',
            timeout: 10000,
            signal
          });
          return {
            pageNum,
            imageUrl: URL.createObjectURL(response.data)
          };
        } catch (error) {
          if (signal && signal.aborted) {
            // propagate abort
            throw new Error('aborted');
          }
          if (attempt === retries) {
            failedPages.push(pageNum);
            return {
              pageNum,
              imageUrl: null,
              error: `Failed to load page ${pageNum}`
            };
          }
          // small backoff
          await new Promise(res => setTimeout(res, 500));
        }
      }
    };
    try {
      // reduced chunk size to avoid creating too many concurrent requests
      const chunkSize = 8;
      const totalChunks = Math.ceil(totalPages / chunkSize);
      for (let chunk = 0; chunk < totalChunks; chunk++) {
        const startPage = chunk * chunkSize + 1;
        const endPage = Math.min((chunk + 1) * chunkSize, totalPages);
        const chunkPromises = [];
        for (let i = startPage; i <= endPage; i++) {
          chunkPromises.push(fetchPageWithRetry(i));
        }
        try {
          // run chunk with limited concurrency
          const chunkPages = await Promise.all(chunkPromises);
          pages.push(...chunkPages);
          setAllPages([...pages]);
        } catch (chunkError) {
          // if aborted, stop early
          if (chunkError && chunkError.message === 'aborted') break;
          console.error(`Error loading chunk ${chunk + 1}:`, chunkError);
        }
        // yield to event loop so UI can update
        await new Promise(res => setTimeout(res, 60));
      }
      if (failedPages.length > 0) {
        setError(`Failed to load ${failedPages.length} page(s). Try refreshing or switching to Single Page mode.`);
      }
    } catch (error) {
      if (error && error.message === 'aborted') {
        console.log('loadAllPages aborted');
      } else {
        console.error('Error loading pages:', error);
        setError('Failed to load pages for continuous view');
      }
    } finally {
      setLoading(false);
      // clear controller
      try { delete window.__loadAllPagesController; } catch (e) {}
    }
  };

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === 'continuous') {
      loadAllPages();
    } else {
      // if switching away from continuous, abort any in-flight continuous loads
      try {
        if (window.__loadAllPagesController) window.__loadAllPagesController.abort();
        delete window.__loadAllPagesController;
      } catch (e) {}
    }
  };

  // Sidebar/bookmark links: go to actual page
  const handleJump = (page, q) => {
    const url = q ? `?page=${page}&q=${encodeURIComponent(q)}` : `?page=${page}`;
    window.open(url, '_blank');
  };

  // Internal document links: add offset
  const handleInternalLink = (page) => {
    const correctedPage = page + 2; // adjusted offset: previously +3, changed to +2 to correct off-by-one
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    const url = q ? `?page=${correctedPage}&q=${encodeURIComponent(q)}` : `?page=${correctedPage}`;
    window.open(url, '_blank');
  };

  return (
    <div className="app-container">
      {/* Search Results Modal */}
      {searchModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px #2222', padding: 32, minWidth: 600, maxHeight: '80vh', overflowY: 'auto', position: 'relative' }}>
            <button style={{ position: 'absolute', top: 16, right: 16, fontSize: 18, background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setSearchModalOpen(false)}>&times;</button>
            <h3 style={{ color: '#7c6f57', marginBottom: 12, fontSize: '1.1em' }}>Search Results</h3>
            <div style={{ marginBottom: 8, color: '#444', fontSize: '0.98em', background: '#fffbe6', padding: 8, borderRadius: 6 }}>
              <strong>How search works:</strong> Each result shows all lines on the page that contain your search terms. If a page matches, you may need to visually scan the page for context. Search terms are highlighted below.
            </div>
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ fontSize: '0.95em', color: '#666' }}>
                Showing {results.length} of {searchTotalResults} result{searchTotalResults !== 1 ? 's' : ''}
              </span>
              <span style={{ fontSize: '0.95em', color: '#666' }}>
                Page {searchPage} of {searchTotalPages}
              </span>
              <button onClick={() => handleSearchPageChange(searchPage - 1)} disabled={searchPage <= 1} style={{ padding: '4px 10px', borderRadius: 4 }}>Prev</button>
              <button onClick={() => handleSearchPageChange(searchPage + 1)} disabled={searchPage >= searchTotalPages} style={{ padding: '4px 10px', borderRadius: 4 }}>Next</button>
              <label style={{ marginLeft: 8 }}>Page Size:</label>
              <select value={searchPageSize} onChange={handleSearchPageSizeChange} style={{ padding: '2px 8px', borderRadius: 4 }}>
                {[25, 50, 100, 200, 500].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <div style={{ maxHeight: 400, minWidth: 500, overflowY: 'auto' }}>
              {results.map((r, idx) => (
                <div key={idx} className="search-result" style={{ marginBottom: 8, padding: 8, backgroundColor: '#fff', borderRadius: 4 }}>
                  <span className="anchor-link" onClick={() => handleJump(r.page, search)} style={{ fontWeight: 600, color: '#1976d2', cursor: 'pointer' }}>
                    Page {r.page}
                  </span>
                  {/* Always show highlighted lines if present, otherwise highlight content */}
                  {r.lines && r.lines.length > 0 ? (
                    <div style={{ marginTop: 4 }}>
                      {r.lines.map((line, i) => (
                        <div key={i} style={{ color: '#333', fontStyle: 'italic', marginBottom: 2 }}>
                          <span dangerouslySetInnerHTML={{ __html: highlightTerms(line, search) }} />
                        </div>
                      ))}
                    </div>
                  ) : r.content && (
                    <div style={{ marginTop: 4, color: '#333', fontStyle: 'italic', marginBottom: 2 }}>
                      <span dangerouslySetInnerHTML={{ __html: highlightTerms(r.content, search) }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Search Help Modal */}
      {showSearchHelp && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.4)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px #2222', padding: 24, minWidth: 520, maxWidth: '90%', maxHeight: '80vh', overflowY: 'auto', position: 'relative' }}>
            <button style={{ position: 'absolute', top: 12, right: 12, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setShowSearchHelp(false)}>&times;</button>
            <h3 style={{ marginTop: 4, color: '#333' }}>How search works</h3>
            <p style={{ color: '#444', lineHeight: 1.4 }}>
              You can search the PDF in a few ways:
            </p>
            <ul style={{ color: '#444' }}>
              <li><strong>Quoted phrase</strong>: wrap a phrase in quotes to match it exactly, including short words. Example: <code>"to be or not to be"</code></li>
              <li><strong>Single word</strong>: enter a single word (e.g. <code>Blake</code>) — the search prefers whole-word matches.</li>
              <li><strong>Multiple terms</strong>: enter multiple words (without quotes) to match any of them; results show lines containing any matched term.</li>
            </ul>
            <div style={{ marginTop: 12, padding: 12, background: '#fffbe6', borderRadius: 6, color: '#333' }}>
              Example searches:
              <div style={{ marginTop: 8 }}>
                  <code>"William Blake"</code> — exact phrase match (will match the phrase including short words)<br/>
                <code>is</code> — single-word whole-word match (won't match inside other words)
              </div>
            </div>
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button onClick={() => setShowSearchHelp(false)} style={{ padding: '8px 12px', borderRadius: 6, background: '#1976d2', color: '#fff', border: 'none' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* ...existing sidebar and main viewer code... */}
      <aside className="sidebar">
        <button
          style={{ margin: '12px 0', padding: '8px 16px', borderRadius: 6, background: '#1976d2', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}
          onClick={() => handleJump(1)}
        >
          Return to Title Page/Home
        </button>
        {(() => {
          const removeTitles = [
             "Contents0F",
             "Citations, Annotations, and Links 19",
             "User Note",
             "Abbreviations",
             "William Blake’s Works",
             "Secondary Texts and Notes",
             "Acknowledgments",
             "Preface",
             "Introduction",
             "Citations, Annotations, and Links",
             "A Note on Specialized Terms for Researchers New to William Blake",
             "Different Blake Journals",
             "Document Bookmarks",
             "A Selected Annotated Bibliography of William Blake and His Circle: A Guide to Further Research"
           ];
           const superscriptPattern = /^\d+[a-zA-Z]*f{1,2}$|^\d+[a-zA-Z]$|^\d+$/;
           // First filter out explicit removeTitles and superscript-like titles
           const preFiltered = bookmarks.filter(bm => !removeTitles.includes(bm.title) && !superscriptPattern.test((bm.title || '').trim()));
           // Recursive check: return true if any descendant (at any depth) normalizes to the parent norm
           function descendantHasSameNorm(children, parentNorm) {
             if (!children || children.length === 0) return false;
             for (const c of children) {
               const childNorm = normalizeTitle(cleanBookmarkTitle(c.title || ''));
               if (childNorm === parentNorm) return true;
               if (c.children && c.children.length > 0) {
                 if (descendantHasSameNorm(c.children, parentNorm)) return true;
               }
             }
             return false;
           }
           // Then remove any top-level parent that contains a descendant with the same normalized/cleaned title
           const filtered = preFiltered.filter(bm => {
             if (!bm.children || bm.children.length === 0) return true;
             const parentNorm = normalizeTitle(cleanBookmarkTitle(bm.title || ''));
             // if any descendant normalizes to same as parent, drop the parent to avoid duplicate menus
             if (descendantHasSameNorm(bm.children, parentNorm)) return false;
             return true;
           });

           // Deduplicate top-level entries by normalized title.
           // If two top-level entries normalize to the same title, prefer the one that has children.
           const seen = new Map(); // norm -> index in deduped
           const deduped = [];
           filtered.forEach(bm => {
             const norm = normalizeTitle(cleanBookmarkTitle(bm.title || ''));
             const hasChildren = bm.children && bm.children.length > 0;
             if (!seen.has(norm)) {
               seen.set(norm, deduped.length);
               deduped.push(bm);
             } else {
               const idx = seen.get(norm);
               const existing = deduped[idx];
               const existingHasChildren = existing.children && existing.children.length > 0;
               // prefer the one with children
               if (!existingHasChildren && hasChildren) {
                 deduped[idx] = bm;
               }
               // otherwise keep existing
             }
           });

           const finalList = deduped;
           // find Table of Contents item (normalized) and remove it from finalList so we can render it above PrefatoryMaterialTree
           const tocNorm = 'table of contents';
           let tocItem = null;
           const remaining = [];
           for (const bm of finalList) {
             const norm = normalizeTitle(cleanBookmarkTitle(bm.title || ''));
             if (!tocItem && norm === tocNorm) {
               tocItem = bm;
             } else {
               remaining.push(bm);
             }
           }

           // Build rendered elements: Table of Contents (if present), PrefatoryMaterialTree, then remaining bookmarks
           const rendered = [];
           if (tocItem) {
             rendered.push(
               <CollapsibleSection
                 key={tocItem.title + '-toc'}
                 title={cleanBookmarkTitle(tocItem.title)}
                 defaultOpen={false}
                 onTitleClick={tocItem.page ? () => handleJump(tocItem.page) : undefined}
               >
                 {tocItem.children && tocItem.children.length > 0 ? (
                   <BookmarkTree bookmarks={tocItem.children} onJump={handleJump} />
                 ) : null}
               </CollapsibleSection>
             );
           }

           // Prefatory material always next
           rendered.push(<PrefatoryMaterialTree key="prefatory" onJump={handleJump} />);

           // Then render the rest
           return rendered.concat(remaining.map((bm, idx, arr) => {
             // Insert a blank line between Appendix B and Appendix C
             if (
               cleanBookmarkTitle(bm.title).toLowerCase().startsWith('appendix c') &&
               idx > 0 && cleanBookmarkTitle(arr[idx - 1].title).toLowerCase().startsWith('appendix b')
             ) {
               return [
                 <div key={bm.title + '-spacer'} style={{ height: 16 }} />,
                 // Always render a CollapsibleSection so the arrow appears; include children only if present
                 <CollapsibleSection
                   key={bm.title}
                   title={cleanBookmarkTitle(bm.title)}
                   defaultOpen={false}
                   onTitleClick={bm.page ? () => handleJump(bm.page) : undefined}
                 >
                   {bm.children && bm.children.length > 0 ? (
                     <BookmarkTree bookmarks={bm.children} onJump={handleJump} />
                   ) : null}
                 </CollapsibleSection>
               ];
             }
             // Always render a CollapsibleSection so the arrow appears; include children only if present
             return (
               <CollapsibleSection
                 key={bm.title}
                 title={cleanBookmarkTitle(bm.title)}
                 defaultOpen={false}
                 onTitleClick={bm.page ? () => handleJump(bm.page) : undefined}
               >
                 {bm.children && bm.children.length > 0 ? (
                   <BookmarkTree bookmarks={bm.children} onJump={handleJump} />
                 ) : null}
               </CollapsibleSection>
             );
           }));
         })()}
       </aside>
      <main className="viewer">
        {/* ...existing header, search bar, and viewer code... */}
        <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '1.8em', fontWeight: 700, color: '#1976d2', letterSpacing: '0.5px', lineHeight: 1.2 }}>
                A Selected Annotated Bibliography of William Blake and His Circle
              </div>
            </div>
          </div>
          {/* Search moved to top right */}
          <div className="search-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, marginTop: 30 }}>
            <div className="search-bar" style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search PDF..."
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', minWidth: 200 }}
                onKeyPress={e => e.key === 'Enter' && handleSearch()}
              />
              <button onClick={handleSearch} style={{ padding: '8px 16px' }}>Search</button>
              <button
                onClick={() => setShowSearchHelp(true)}
                title="Search help"
                aria-label="Search help"
                style={{ marginLeft: 6, padding: '6px 10px', borderRadius: 6, border: '1px solid #bdbdbd', background: '#e0e0e0', color: '#222', fontWeight: 600, cursor: 'pointer' }}
              >
                ?
              </button>
            </div>
            {/* help opens when search input is focused */}
            {results.length > 0 && (
              <div style={{ fontSize: '0.9em', color: '#666' }}>
                {results.length} result{results.length !== 1 ? 's' : ''} found
              </div>
            )}
          </div>
        </div>
        {/* Main Page Content Area */}
        <div className="page-content" style={{ flexGrow: 1 }}>
          {/* Single Page View */}
          {viewMode === 'single' && (
            <div>
              <h3 style={{ color: '#1976d2', marginBottom: 12, fontWeight: 600 }}>Page {pageNum}</h3>
              {loading ? (
                <div>Loading...</div>
              ) : (
                pageImg && (
                  <SelectablePageViewer 
                    pageNum={pageNum}
                    imageUrl={pageImg}
                    highlightQuery={highlightQuery}
                    style={{ 
                      maxWidth: '900px', 
                      width: '100%', 
                      boxShadow: '0 4px 24px #e0e0e0', 
                      border: '2px solid #1976d2', 
                      background: '#fff' 
                    }}
                    onNavigate={target => {
                      if (typeof target === 'number') {
                        handleInternalLink(target);
                      }
                    }}
                  />
                )
              )}
              <div className="nav-buttons">
                <button 
                  onClick={() => setPageNum(pageNum > 1 ? pageNum - 1 : 1)}
                  disabled={pageNum <= 1}
                >
                  &lt; Prev
                </button>
                <span style={{ margin: '0 12px', color: '#666' }}>
                  Page {pageNum} of {totalPages}
                </span>
                <button 
                  onClick={() => setPageNum(pageNum < totalPages ? pageNum + 1 : totalPages)}
                  disabled={pageNum >= totalPages}
                >
                  Next &gt;
                </button>
              </div>
            </div>
          )}
          {/* Continuous View */}
          {viewMode === 'continuous' && (
            <div>
              <h3 style={{ color: '#1976d2', marginBottom: 12, fontWeight: 600 }}>
                Continuous View {allPages.length > 0 && `(${allPages.length}/${totalPages} pages loaded)`}
              </h3>
              {loading && allPages.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center' }}>
                  <div>Loading pages for continuous view...</div>
                  <div style={{ fontSize: '0.9em', color: '#666', marginTop: 8 }}>
                    This may take a moment...
                  </div>
                </div>
              ) : (
                <div style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                  {allPages.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
                      Click "Continuous Scroll" to load all pages
                    </div>
                  ) : (
                    <VirtualizedPageList
                      pages={allPages}
                      width={960}
                      containerHeight={Math.min(window.innerHeight * 0.8, 1000)}
                      highlightQuery={highlightQuery}
                      onNavigate={(target) => {
                        if (typeof target === 'number') handleInternalLink(target);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
// ...existing code...
}

// Highlight search terms in a line (case-insensitive)



function highlightTerms(line, search) {
  if (!search) return line;
  let cleanSearch = search.trim();
  const isQuoted =
    (cleanSearch.startsWith('"') && cleanSearch.endsWith('"')) ||
    (cleanSearch.startsWith("'") && cleanSearch.endsWith("'"));
  if (isQuoted) {
    cleanSearch = cleanSearch.slice(1, -1);
  }
  if (!cleanSearch) return line;

  // Helper: highlight text in a DOM node tree, preserving tags
  function highlightInNode(node, regex) {
    if (node.nodeType === 3) { // Text node
      const parts = node.data.split(regex);
      if (parts.length === 1) return node.data;
      let result = [];
      let match;
      let lastIndex = 0;
      regex.lastIndex = 0;
      for (let i = 0; i < parts.length; i++) {
        if (parts[i]) result.push(parts[i]);
        if (i < parts.length - 1) {
          match = regex.exec(node.data.slice(lastIndex));
          if (match) {
            result.push(`<mark style="background: #ffe066; color: #222;">${match[0]}</mark>`);
            lastIndex += match.index + match[0].length;
          }
        }
      }
      return result.join('');
    } else if (node.nodeType === 1) { // Element node
      let html = '';
      for (let child of node.childNodes) {
        html += highlightInNode(child, regex);
      }
      return node.tagName === 'A'
        ? `<a href="${node.getAttribute('href')}"${node.getAttribute('target') ? ` target="${node.getAttribute('target')}"` : ''}>${html}</a>`
        : `<${node.tagName.toLowerCase()}${[...node.attributes].map(a => ` ${a.name}="${a.value}"`).join('')}>${html}</${node.tagName.toLowerCase()}>`;
    }
    return '';
  }

  // Build regex for phrase or word highlighting
  let regex;
  if (isQuoted) {
    const words = cleanSearch.split(/\s+/).filter(Boolean);
    if (words.length > 1) {
      let phrasePattern;
      try {
        phrasePattern = words.map(w => w.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')).join('[\\s\\p{P}]+');
        regex = new RegExp(phrasePattern, 'giu');
      } catch (e) {
        phrasePattern = words.map(w => w.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')).join('[\\s\\W]+');
        regex = new RegExp(phrasePattern, 'gi');
      }
    } else {
      regex = new RegExp(cleanSearch.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&'), 'gi');
    }
  } else {
    const terms = cleanSearch.split(/\s+/).filter(Boolean).map(term => term.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&'));
    if (terms.length === 0) return line;
    regex = new RegExp(terms.join('|'), 'gi');
  }

  // Parse HTML and walk DOM
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${line}</div>`, 'text/html');
  const root = doc.body.firstChild;
  return highlightInNode(root, regex);
}

export default App;
